import { CloseCircleFilled } from '@ant-design/icons'
import { t } from '@lingui/core/macro'
import { ActionButton, CancelActionButton, SaveActionButton } from '@pkg/ui/button'
import { Alert, Steps as AntSteps, Tabs as AntTabs, Divider, Flex, Popconfirm, Space, Typography } from 'antd'
import useToken from 'antd/es/theme/useToken'
import { createContext, type ReactNode, useContext } from 'react'
import { FaSave } from 'react-icons/fa'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa6'
import type { SectionFlowReturn } from './useSectionFlow'

interface SectionFlowContextValue {
  flow: SectionFlowReturn
  onSubmit: () => void
  onCancel: () => void
}

const SectionFlowContext = createContext<SectionFlowContextValue | null>(null)

const useSectionFlowContext = () => {
  const ctx = useContext(SectionFlowContext)
  if (!ctx) throw new Error('SectionFlow compound components must be used inside <SectionFlow>')
  return ctx
}

// ── SectionFlow (provider) ───────────────────────────────────────────

interface SectionFlowProps {
  flow: SectionFlowReturn
  onSubmit: () => void
  onCancel: () => void
  children: ReactNode
}

// biome-ignore lint/style/useComponentExportOnlyModules: exported via Object.assign compound pattern
function SectionFlowRoot({ flow, onSubmit, onCancel, children }: SectionFlowProps) {
  return <SectionFlowContext value={{ flow, onSubmit, onCancel }}>{children}</SectionFlowContext>
}

// ── SectionFlow.Step ─────────────────────────────────────────────────

interface StepProps {
  name: string
  children: ReactNode
  className?: string
}

function Step({ name, children, className }: StepProps) {
  const { flow } = useSectionFlowContext()

  if (flow.mode === 'tabbed') {
    if (!flow.included.has(name)) return null
    return (
      <div data-testid={`step-${name}`} className={className}>
        {children}
      </div>
    )
  }

  if (flow.mode === 'focused') {
    if (name !== flow.active) return null
    return (
      <div data-testid={`step-${name}`} className={className}>
        {children}
      </div>
    )
  }

  // sequential
  const isActive = name === flow.active
  const hasBeenVisited = flow.visited.has(name)

  if (!isActive && !hasBeenVisited) return null

  return (
    <div data-testid={`step-${name}`} style={{ display: isActive ? undefined : 'none' }} className={className}>
      {children}
    </div>
  )
}

// ── SectionFlow.Tab(s) ───────────────────────────────────────────────

interface TabProps {
  name: string
  title?: ReactNode
  children: ReactNode
  className?: string
  innerTitle?: ReactNode
  alert?: {
    type?: 'info' | 'warning' | 'error' | 'success'
    title: ReactNode
    closable?: boolean
  }
}

interface TabsProps {
  items: TabProps[]
}

function Tabs({ items: tabs }: TabsProps) {
  const { flow } = useSectionFlowContext()

  const visibleTabs = tabs.filter(tab => {
    if (flow.mode === 'tabbed') return flow.included.has(tab.name)
    if (flow.mode === 'focused') return tab.name === flow.active
    return true
  })

  return (
    <AntTabs
      activeKey={flow.active}
      tabPlacement={'start'}
      onChange={key => flow.goTo(key)}
      items={visibleTabs.map(tab => ({
        key: tab.name,
        label: tab.title,
        children: (
          <Step name={tab.name} className={tab.className}>
            <Flex vertical gap="small">
              {tab.innerTitle && <Typography.Title level={5}>{tab.innerTitle}</Typography.Title>}
              {tab.alert && (
                <Alert
                  type={tab.alert.type ?? 'info'}
                  closable={tab.alert.closable ?? true}
                  showIcon
                  title={tab.alert.title}
                />
              )}
              {tab.children}
            </Flex>
          </Step>
        ),
        disabled: flow.mode === 'sequential' && !flow.visited.has(tab.name) && tab.name !== flow.active,
      }))}
    />
  )
}

// ── SectionFlow.Sections ─────────────────────────────────────────────

/**
 * Dispatches the section list by mode: `sequential` renders bare steps (create wizard,
 * one active step at a time), `tabbed` renders the vertical AntD tab rail. Lets a drawer
 * declare its sections once instead of branching `{isCreate ? Steps : Tabs}`.
 */
function Sections({ items }: TabsProps) {
  const { flow } = useSectionFlowContext()

  if (flow.mode === 'tabbed') {
    return (
      <Tabs
        items={items.map(item => ({
          ...item,
          title: item.title ?? flow.sections.find(s => s.name === item.name)?.title,
        }))}
      />
    )
  }

  return items.map(item => (
    <Step key={item.name} name={item.name} className={item.className}>
      <Flex vertical gap="small">
        {item.innerTitle && <Typography.Title level={5}>{item.innerTitle}</Typography.Title>}
        {item.alert && (
          <Alert
            type={item.alert.type ?? 'info'}
            closable={item.alert.closable ?? true}
            showIcon
            title={item.alert.title}
          />
        )}
        {item.children}
      </Flex>
    </Step>
  ))
}

// ── SectionFlow.Steps ────────────────────────────────────────────────

/** Vertical AntD `Steps` rail for `sequential` mode. Visited steps are clickable. */
function Steps() {
  const { flow } = useSectionFlowContext()

  const canNavigate = (name: string) => flow.visited.has(name) || name === flow.active

  return (
    <AntSteps
      orientation="vertical"
      size="small"
      current={flow.currentIndex}
      onChange={index => {
        const name = flow.sections[index]?.name
        if (name && canNavigate(name)) flow.goTo(name)
      }}
      className="border-r border-gray-200 pr-4!"
      items={flow.sections.map(section => ({
        title: section.title,
        disabled: flow.mode === 'sequential' && !canNavigate(section.name),
      }))}
    />
  )
}

// ── SectionFlow.Actions ──────────────────────────────────────────────

interface ActionsProps {
  isSaving: boolean
  canSubmit: boolean
  /** Gate for the current step's "Weiter" action. Defaults to canSubmit for standard steps. */
  canProceed?: boolean
  onCancel?: () => void
  submitConfirm?: {
    title: ReactNode
    description?: ReactNode
    okText?: ReactNode
    cancelText?: ReactNode
  }
}

function Actions({ isSaving, canSubmit, canProceed, onCancel: cancelOverride, submitConfirm }: ActionsProps) {
  const { flow, onSubmit, onCancel } = useSectionFlowContext()
  const handleCancel = cancelOverride ?? onCancel
  const [_, token] = useToken()

  const withSubmitConfirm = (button: ReactNode) => {
    if (!submitConfirm || isSaving || !canSubmit) return button

    return (
      <Popconfirm
        title={submitConfirm.title}
        description={submitConfirm.description}
        okText={submitConfirm.okText}
        cancelText={submitConfirm.cancelText}
        onConfirm={onSubmit}
        icon={<CloseCircleFilled style={{ color: token.colorError }} />}
      >
        {button}
      </Popconfirm>
    )
  }

  if (flow.mode === 'focused' || flow.mode === 'tabbed') {
    return (
      <Space>
        <CancelActionButton onClick={handleCancel} />
        <Divider vertical />
        {withSubmitConfirm(
          <SaveActionButton
            disabled={!canSubmit || isSaving}
            loading={isSaving}
            onClick={submitConfirm ? undefined : onSubmit}
          />,
        )}
      </Space>
    )
  }

  const variant = flow.sections[flow.currentIndex]?.variant ?? 'default'
  const canContinue = canProceed ?? canSubmit

  if (variant === 'cancel-only') {
    return (
      <Space>
        <CancelActionButton onClick={handleCancel} />
      </Space>
    )
  }

  if (variant === 'select') {
    return (
      <Space>
        <CancelActionButton onClick={handleCancel} />
        <Divider vertical />
        {flow.canGoPrev && (
          <ActionButton icon={<FaChevronLeft />} onClick={flow.goToPrev}>
            {t`Zurück`}
          </ActionButton>
        )}
        <ActionButton
          variant="solid"
          color="primary"
          icon={<FaChevronRight />}
          iconPlacement="end"
          disabled={canProceed !== true || isSaving}
          onClick={flow.goToNext}
        >
          {t`Weiter`}
        </ActionButton>
      </Space>
    )
  }

  return (
    <Space>
      <CancelActionButton onClick={handleCancel} />
      <Divider vertical />
      {flow.canGoPrev && (
        <ActionButton icon={<FaChevronLeft />} onClick={flow.goToPrev}>
          {t`Zurück`}
        </ActionButton>
      )}
      {flow.canFinishNow &&
        !flow.isLastStep &&
        withSubmitConfirm(
          <SaveActionButton
            color="orange"
            disabled={!canSubmit || isSaving}
            loading={isSaving}
            onClick={submitConfirm ? undefined : onSubmit}
          >
            {t`Speichern & Schließen`}
          </SaveActionButton>,
        )}
      {flow.isLastStep ? (
        withSubmitConfirm(
          <ActionButton
            variant="solid"
            color="primary"
            icon={flow.sections[flow.currentIndex]?.finishIcon ?? <FaSave />}
            disabled={!canSubmit || isSaving}
            loading={isSaving}
            onClick={submitConfirm ? undefined : onSubmit}
          >
            {flow.sections[flow.currentIndex]?.finishLabel ?? t`Speichern`}
          </ActionButton>,
        )
      ) : (
        <ActionButton
          variant="solid"
          color="primary"
          icon={<FaChevronRight />}
          iconPlacement="end"
          disabled={!canContinue || isSaving}
          loading={isSaving}
          onClick={flow.goToNext}
        >
          {t`Weiter`}
        </ActionButton>
      )}
    </Space>
  )
}

// ── Export ────────────────────────────────────────────────────────────

export const SectionFlow = Object.assign(SectionFlowRoot, {
  Step,
  Tabs,
  Sections,
  Steps,
  Actions,
})

export { useSectionFlowContext }
